import { create } from 'zustand';
import {
  DepartmentFilter,
  DepartmentModel,
  DepartmentCreateModel,
  DepartmentUpdateModel,
} from '../model/department.model';
import axiosInstance from '../../../configs/axios';
import { ApiPath } from '../../../constants/apiPath';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';

export type DepartmentModalName = 'CREATE' | 'UPDATE';

export interface DepartmentActiveModal {
  name: DepartmentModalName;
}

export interface DepartmentControllerState {
  list: DepartmentModel[];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: DepartmentFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: DepartmentActiveModal | null;
  formUpdate: DepartmentUpdateModel | null;
}

export interface DepartmentControllerAction {
  getData: (startLoading?: boolean, filter?: DepartmentFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: DepartmentFilter | null) => void;
  onHandleModal: (activeModal?: DepartmentActiveModal | null) => void;
  onUpdateFormUpdate: (form: DepartmentUpdateModel | null) => void;
  onSubmitCreate: (form: DepartmentCreateModel) => void;
  onSubmitUpdate: (form: DepartmentUpdateModel) => void;
  onDelete: (uuid: string) => void;
}

const DepartmentController = create<DepartmentControllerState & DepartmentControllerAction>()(
  (set) => ({
    loading: false,
    list: [],
    message: null,
    listCount: 0,
    page: 0,
    pageRowAmount: 10,
    filter: { skip: 0, take: 10 },
    activeModal: null,
    formUpdate: null,

    getData: async (startLoading?: boolean, filter?: DepartmentFilter | null) => {
      try {
        if (startLoading) set(() => ({ loading: true }));
        const res = await axiosInstance.get(ApiPath.departments.all, {
          params: filter ?? { skip: 0, take: 10 },
        });
        const list: DepartmentModel[] = res?.data?.data ?? [];
        const listCount: number = res?.data?.meta?.total ?? res?.data?.count ?? 0;
        set(() => ({ list, listCount }));
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    onPageChange: (page: number, rows: number) => {
      set(() => ({ pageRowAmount: rows, page }));
    },

    onFilterChange(filter?: DepartmentFilter | null) {
      set(() => ({ filter: filter ?? null }));
      this.getData(true, filter);
    },

    onHandleModal: (activeModal?: DepartmentActiveModal | null) => {
      set(() => ({ activeModal: activeModal ?? null, message: null }));
      if (!activeModal) set(() => ({ formUpdate: null }));
    },

    onUpdateFormUpdate: (form: DepartmentUpdateModel | null) => {
      set(() => ({ formUpdate: form, message: null }));
    },

    async onSubmitCreate(form: DepartmentCreateModel) {
      try {
        set(() => ({ loading: true, message: null }));
        const res = await axiosInstance.post(ApiPath.departments.create, form);
        if (res?.status === 201) {
          set(() => ({ filter: null, page: 0 }));
          this.getData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    async onSubmitUpdate(form: DepartmentUpdateModel) {
      try {
        set(() => ({ loading: true, message: null }));
        const { id, uuid, ...body } = form;
        const res = await axiosInstance.patch(ApiPath.departments.update(id ?? uuid ?? ''), body);
        if (res?.status === 200) {
          set(() => ({ formUpdate: null, filter: null, page: 0 }));
          this.getData(true, null);
          setTimeout(() => this.onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    async onDelete(uuid: string) {
      try {
        set(() => ({ loading: true }));
        const res = await axiosInstance.delete(ApiPath.departments.delete(uuid));
        if (res?.status === 200) {
          this.getData(true, DepartmentController.getState().filter);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },
  })
);

export default DepartmentController;
